import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'lobby_coach_dismissed_v1';

/**
 * Friendly "How do I play?" coach banner shown on the Lobby for new/older
 * players. Pulses gently to draw the eye, expands when tapped, and lets the
 * player dismiss it forever (per browser / PWA install).
 *
 * Why a separate component (and not a corner-of-screen toast):
 *  - Older players need it ABOVE the fold, in-flow, not a tiny popover
 *  - We don't want it covering the actual game tiles
 *  - It should look like part of the lobby, not a temporary nag
 */
const LobbyHelpCoach = () => {
    const { t } = useTranslation();
    const [dismissed, setDismissed] = useState(false);
    const [open, setOpen] = useState(true);

    useEffect(() => {
        try {
            if (localStorage.getItem(STORAGE_KEY) === '1') setDismissed(true);
        } catch { /* localStorage may be disabled */ }
    }, []);

    if (dismissed) return null;

    const dismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative rounded-2xl overflow-hidden border border-[#FFD700]/40"
            style={{
                background: 'linear-gradient(135deg, rgba(255,215,0,0.10) 0%, rgba(255,0,127,0.06) 50%, rgba(0,0,0,0.4) 100%)',
            }}
            data-testid="lobby-help-coach"
        >
            {/* Soft animated glow on the edge */}
            <motion.div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: 'radial-gradient(circle at 12% 50%, rgba(255,215,0,0.25), transparent 55%)',
                }}
                animate={{ opacity: [0.55, 0.85, 0.55] }}
                transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
            />

            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="relative z-10 w-full text-left px-4 sm:px-5 py-3 flex items-center gap-3"
                data-testid="lobby-help-coach-toggle"
            >
                <div className="w-10 h-10 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/40 flex items-center justify-center shrink-0">
                    <Sparkles className="w-5 h-5 text-[#FFD700]" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-base sm:text-lg leading-tight">
                        {t('coach.new_here')}
                    </p>
                    <p className="text-[#A0AAB2] text-xs sm:text-sm leading-tight mt-0.5">
                        {t('coach.summary')}
                    </p>
                </div>
                <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
                    <ChevronDown className="w-5 h-5 text-[#FFD700]" />
                </motion.div>
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        key="body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="relative z-10 overflow-hidden"
                    >
                        <div className="px-4 sm:px-5 pb-4 grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-[13px]">
                            <Step
                                num="1"
                                color="#39FF14"
                                title={t('coach.step1_title')}
                                desc={t('coach.step1_desc')}
                                testid="coach-step-1"
                            />
                            <Step
                                num="2"
                                color="#FFD700"
                                title={t('coach.step2_title')}
                                desc={t('coach.step2_desc')}
                                testid="coach-step-2"
                            />
                            <Step
                                num="3"
                                color="#FF007F"
                                title={t('coach.step3_title')}
                                desc={t('coach.step3_desc')}
                                testid="coach-step-3"
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); dismiss(); }}
                className="absolute top-2 right-2 z-20 w-7 h-7 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-[#A0AAB2] hover:text-white"
                title="Hide this"
                data-testid="lobby-help-coach-dismiss"
            >
                <X className="w-3.5 h-3.5" />
            </button>
        </motion.div>
    );
};

const Step = ({ num, color, title, desc, testid }) => (
    <div
        className="rounded-xl bg-black/40 border border-white/10 px-3 py-2.5 flex gap-2.5"
        data-testid={testid}
    >
        <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-black shrink-0"
            style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}
        >
            {num}
        </div>
        <div className="min-w-0">
            <p className="text-white font-bold leading-tight">{title}</p>
            <p className="text-[#A0AAB2] text-xs leading-snug mt-0.5">{desc}</p>
        </div>
    </div>
);

export default LobbyHelpCoach;
